import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import { UNSUPPORTED_VOUCHER_TYPES, toUiCoupon, toVoucherTemplatePatch } from '../adapters';
import { CouponScreen } from '../../screens/CouponScreen';
import type { Coupon } from '../../screens/loyaltyData';
import type { VoucherTemplate } from '../bilClient';

/**
 * ตัวต่อของหน้าคูปอง
 *
 * ⚠️ คูปองอยู่ใน **BIL module** ไม่ใช่ CRM (`/apis/BIL/voucher-template/`)
 *    ทั้งสองโหมดยิงได้แล้ว — standalone ผ่าน bilClient · embed ผ่าน API client ของ host
 */
export function CouponContainer() {
  const { api, dataMode } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [templates, setTemplates] = useState<VoucherTemplate[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');

    api.listVoucherTemplates()
      .then((page) => {
        if (cancelled) return;
        setTemplates(page.items);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [isMock, api]);

  useEffect(() => load(), [load]);

  const coupons = useMemo(
    () => (templates ? templates.map(toUiCoupon) : undefined),
    [templates],
  );

  /**
   * คูปองชนิดที่ backend มีแต่หน้าจอนี้ยังไม่รองรับ (แถมของฟรี · ให้คะแนน)
   * แสดงในตารางได้ แต่กดแก้ไม่ได้ — ซ่อนไปเลยจะทำให้คนตั้งค่าไม่รู้ว่ามีอยู่
   */
  const lockedIds = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of templates ?? []) {
      if (UNSUPPORTED_VOUCHER_TYPES.includes(t.voucherType)) {
        map.set(
          String(t.id),
          `คูปองชนิด "${t.voucherTypeLabel}" ยังไม่เปิดให้แก้ในหน้านี้`,
        );
      }
    }
    return map;
  }, [templates]);

  const onSave = useCallback(
    async (c: Coupon) => {
      if (isMock) return;

      const patch = toVoucherTemplatePatch(c);
      const id = Number(c.id);
      const isNew = !(Number.isFinite(id) && id > 0);

      const prev = templates?.find((t) => String(t.id) === c.id);

      /* รูปเดิมถูกเอาออก — ล้างด้วย PATCH ปกติ ดูจากรายการที่โหลดมา */
      if (!isNew && prev?.image && !c.imageUrl) patch.image = null;

      /**
       * ⚠️ **ห้ามส่งโค้ดที่ไม่ได้แก้** — ถ้าส่งไป ระบบหลังบ้านจะไล่เขียนโค้ดนี้ทับ
       *    คูปองทุกใบที่ผูกกับเทมเพลต รวมถึงใบที่ลูกค้าถืออยู่ แล้วชนกันเองจนพัง
       *    (Sentry: MEHEALTH-STAGING-API-EQ) การไม่ส่งค่าที่ผู้ใช้ไม่ได้แตะถูกต้องกว่าอยู่แล้ว
       *
       * ⛔ ถ้าผู้ใช้**ตั้งใจเปลี่ยนโค้ด**ของคูปองที่มีคนถืออยู่ ยังพังเหมือนเดิม
       *    จนกว่าทีม backend จะแก้ฝั่งนั้น
       */
      if (!isNew && prev && patch.voucherCode === prev.voucherCode) delete patch.voucherCode;

      const saved = isNew
        ? await api.createVoucherTemplate(patch)
        : await api.updateVoucherTemplate(id, patch);

      /* อัปโหลดรูปหลังบันทึกสำเร็จ เพราะคูปองใหม่เพิ่งมี id ตอนนี้ */
      if (c.imageFile) {
        try {
          await api.uploadVoucherTemplateImage(saved.id, c.imageFile);
        } catch (err) {
          const page = await api.listVoucherTemplates();
          setTemplates(page.items);
          throw new Error(
            err instanceof Error
              ? `บันทึกคูปองแล้ว แต่อัปโหลดรูปไม่สำเร็จ — ${err.message}`
              : 'บันทึกคูปองแล้ว แต่อัปโหลดรูปไม่สำเร็จ',
          );
        }
      }

      /* โหลดใหม่เพื่อให้ได้ค่าที่ backend คำนวณ (computed_status · remaining_quota) */
      const page = await api.listVoucherTemplates();
      setTemplates(page.items);
    },
    [isMock, api, templates],
  );

  if (isMock) return <CouponScreen initialState="ready" />;

  return (
    <CouponScreen
      initialState={state}
      coupons={coupons ?? []}
      onSave={onSave}
      onRefresh={load}
      refreshing={state === 'loading'}
      lockedIds={lockedIds}
      /* endpoint รายการไม่ส่งรูปกลับมา อัปโหลดแล้วรูปจะหายเมื่อโหลดใหม่ — ปิดไว้ก่อน */
      showImage={false}
    />
  );
}
